// SelectedRoomCard
//
// Shown beneath the "Pick a room" select on the Check In / Out and
// Reservations forms so the operator can see exactly what they just
// chose: room type, bed, view, occupancy, size, and full amenity list.
// Renders nothing when no room is selected.

import React from "react";
import { AMENITY_BY_KEY, parseAmenities } from "../utils/amenities";

const STATUS_COLOR = {
  available:    { dot: "#4a7c59", bg: "rgba(74,124,89,.12)",   color: "#2d6a4f" },
  cleaning:     { dot: "#c8a05a", bg: "rgba(200,160,90,.14)",  color: "#8a6a30" },
  reserved:     { dot: "#3a7bd5", bg: "rgba(58,123,213,.12)",  color: "#1a5a8a" },
  occupied:     { dot: "#a93226", bg: "rgba(169,50,38,.10)",   color: "#a93226" },
  maintenance:  { dot: "#e67e22", bg: "rgba(230,126,34,.10)",  color: "#a85e16" },
  out_of_order: { dot: "#7a4a4a", bg: "rgba(122,74,74,.12)",   color: "#6a3a3a" },
};

export default function SelectedRoomCard({ room, money }) {
  if (!room) return null;
  const amen = parseAmenities(room.amenities);
  const status = STATUS_COLOR[room.status] || STATUS_COLOR.available;
  const fmtMoney = money || ((n) => `GH₵ ${Number(n || 0).toFixed(2)}`);

  return (
    <div style={{
      marginTop: 4,
      borderRadius: 10,
      overflow: "hidden",
      border: "1px solid rgba(200,160,90,.32)",
      background: "linear-gradient(135deg, #fdf8f2 0%, #f8edd8 100%)",
    }}>
      {/* Header strip */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 10, padding: "10px 14px",
        background: "linear-gradient(180deg, rgba(200,160,90,.18) 0%, rgba(200,160,90,.08) 100%)",
        borderBottom: "1px solid rgba(200,160,90,.25)",
      }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 24, color: "#2c1f14", fontWeight: 500, lineHeight: 1,
          }}>
            Room {room.room_number}
          </span>
          {room.room_type_name && (
            <span style={{
              fontSize: 11.5, letterSpacing: ".12em", textTransform: "uppercase",
              color: "#8a6a30", fontWeight: 500,
            }}>
              · {room.room_type_name}
            </span>
          )}
        </div>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "4px 10px", borderRadius: 999,
          background: status.bg, color: status.color,
          fontSize: 11, letterSpacing: ".1em", textTransform: "uppercase", fontWeight: 500,
        }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: status.dot }} />
          {(room.status || "available").replace("_", " ")}
        </span>
      </div>

      {/* Body */}
      <div style={{ padding: "12px 14px" }}>
        {/* Facts row */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: "8px 14px", marginBottom: amen.length > 0 || room.description ? 12 : 0,
        }}>
          <Fact label="Nightly" value={fmtMoney(room.price_per_night)} />
          {room.bed_type && <Fact label="Bed" value={room.bed_type} />}
          {room.max_occupancy && <Fact label="Sleeps" value={`${room.max_occupancy} guest${room.max_occupancy === 1 ? "" : "s"}`} />}
          {room.view_type && <Fact label="View" value={room.view_type} />}
          {room.floor && <Fact label="Floor" value={room.floor} />}
          {room.size_sqm && <Fact label="Size" value={`${room.size_sqm} m²`} />}
        </div>

        {/* Amenities */}
        {amen.length > 0 && (
          <div style={{ marginBottom: room.description ? 12 : 0 }}>
            <div style={{
              fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase",
              color: "#6b5142", fontWeight: 500, marginBottom: 6,
            }}>
              Amenities
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {amen.map(k => {
                const a = AMENITY_BY_KEY[k];
                return (
                  <span key={k} style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    padding: "4px 9px", borderRadius: 999,
                    background: "rgba(255,255,255,.7)",
                    border: "1px solid rgba(180,140,80,.28)",
                    fontSize: 11.5, color: "#3a2a1a",
                  }}>
                    <span style={{ fontSize: 12 }}>{a?.icon || "•"}</span>
                    {a?.label || k}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Description */}
        {room.description && (
          <div style={{
            fontSize: 12.5, color: "#4a3828", lineHeight: 1.55,
            fontStyle: "italic", borderTop: "1px dashed rgba(180,140,80,.3)",
            paddingTop: 10,
          }}>
            {room.description}
          </div>
        )}
      </div>
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div>
      <div style={{
        fontSize: 9.5, letterSpacing: ".14em", textTransform: "uppercase",
        color: "#8a6a30", fontWeight: 500,
      }}>
        {label}
      </div>
      <div style={{ fontSize: 13.5, color: "#2c1f14", fontWeight: 500, marginTop: 1 }}>
        {value}
      </div>
    </div>
  );
}
